import { useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { styled } from '@mui/system'
import StyledLink from 'components/generic/typography/StyledLink'
import DataGridWithSearchV2 from 'components/generic/datagrid/DataGridWithSearchV2'
import { setLoading } from 'state/loading'
import { setDecimalPrecision } from 'utils/helpers'
import { dateFormatter } from 'utils/data-manipulation/valueFormatter'
import { SORTABLE_BATCH_BILL_FIELDS, BILL_STATUSES } from 'utils/constants'

const GridStyling = styled('div')(({ theme }) => ({
  '& .MuiDataGrid-root': {
    '& .MuiDataGrid-columnHeaderTitle': {
      paddingLeft: theme.spacing(0.75),
      paddingRight: theme.spacing(0),
    },
    '& .MuiDataGrid-row': {
      cursor: 'pointer',
    },
    '& .MuiDataGrid-columnHeader:focus': {
      outline: 'none',
    },
    '& .MuiDataGrid-cell:focus': {
      outline: 'none',
    },
    '& .MuiDataGrid-cell:focus-within': {
      outline: 'none',
    },
  },
}))

const BatchBillsGrid = props => {
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const handleNavigation = params => {
    dispatch(setLoading(true))
    setTimeout(() => {
      navigate(params.row.batchBillLink)
    }, 0)
  }

  const columns = [
    {
      field: SORTABLE_BATCH_BILL_FIELDS.EXTERNAL_ID,
      headerName: 'Batch Bill #',
      flex: 1,
      renderCell: params => (
        <StyledLink
          to={params.row.batchBillLink}
          onClick={event => {
            event.preventDefault()
            event.stopPropagation()
            if (event.metaKey) {
              window.open(params.row.batchBillLink, '_blank')
            } else {
              handleNavigation(params)
            }
          }}
        >
          {params.row[SORTABLE_BATCH_BILL_FIELDS.EXTERNAL_ID]}
        </StyledLink>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 1,
      sortable: false,
    },
    {
      field: 'billCount',
      headerName: '# of Bills',
      headerAlign: 'right',
      align: 'right',
      flex: 1,
      sortable: false,
      renderCell: ({ row }) => {
        if (row?.status?.toUpperCase() === BILL_STATUSES.SAVING.id) return '--'
        return row.billCount
      },
    },
    {
      field: 'batchBillTotal',
      headerName: 'Batch Bill Total',
      flex: 1,
      headerAlign: 'right',
      align: 'right',
      renderCell: ({ row }) => {
        if (row?.status?.toUpperCase() === BILL_STATUSES.SAVING.id) return '--'
        return `${row?.pricePrepend || '$'}${setDecimalPrecision(row?.batchBillTotal, { decimalPrecisionOverride: 2 })}`
      },
      sortable: false,
    },
    {
      field: 'paid',
      headerName: 'Paid',
      flex: 1,
      headerAlign: 'right',
      align: 'right',
      renderCell: ({ row }) => {
        if (row?.status?.toUpperCase() === BILL_STATUSES.SAVING.id) return '--'
        return `${row?.pricePrepend || '$'}${setDecimalPrecision(row?.paid, { decimalPrecisionOverride: 2 })}`
      },
      sortable: false,
    },
    {
      field: 'currency',
      headerName: 'Currency',
      flex: 1,
      sortable: false,
      renderCell: ({ row }) => {
        if (row?.status?.toUpperCase() === BILL_STATUSES.SAVING.id) return '--'
        return row.currency
      },
    },
    {
      field: SORTABLE_BATCH_BILL_FIELDS.LAST_UPDATED,
      headerName: 'Last Updated',
      type: 'date',
      flex: 1,
      renderCell: ({ row }) => {
        if (row?.status?.toUpperCase() === BILL_STATUSES.SAVING.id) return '--'
        return row?.[SORTABLE_BATCH_BILL_FIELDS.LAST_UPDATED]
          ? dateFormatter(row?.[SORTABLE_BATCH_BILL_FIELDS.LAST_UPDATED])
          : undefined
      },
    },
  ]

  return (
    <GridStyling>
      <DataGridWithSearchV2
        {...props}
        columns={columns}
        onRowClick={(params, event) => {
          if (event.metaKey) {
            window.open(params?.row?.batchBillLink, '_blank')
          } else {
            handleNavigation(params)
          }
        }}
      />
    </GridStyling>
  )
}

export default BatchBillsGrid
